import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Check, Copy } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SEEN_KEY = "prestige_subscribe_seen_v1";
const DISCOUNT_CODE = "RITUAL10";
const HIDE_ON = ["/admin", "/checkout", "/experience", "/login", "/account"];

export const SubscribePopup = () => {
  const { pathname } = useLocation();
  const { customer } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const suppressed = HIDE_ON.some((p) => pathname.startsWith(p)) || (customer && customer !== false);

  useEffect(() => {
    if (suppressed) return;
    if (localStorage.getItem(SEEN_KEY)) return;

    let opened = false;
    const trigger = () => {
      if (opened) return;
      opened = true;
      setOpen(true);
    };
    const timer = setTimeout(trigger, 8000);
    const onExit = (e) => {
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener("mouseout", onExit);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onExit);
    };
  }, [suppressed]);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      localStorage.setItem(SEEN_KEY, "1");
      setDone(true);
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(DISCOUNT_CODE);
    toast.success("Code copied");
  };

  if (suppressed) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="subscribe-backdrop"
            className="fixed inset-0 z-[90] bg-forest-deep/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismiss}
          />
          <motion.div
            data-testid="subscribe-popup"
            className="fixed inset-0 z-[95] flex items-center justify-center p-5 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="pointer-events-auto relative w-full max-w-3xl grid md:grid-cols-2 bg-offwhite rounded-[1.5rem] overflow-hidden shadow-2xl"
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 24 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                data-testid="subscribe-close"
                onClick={dismiss}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-cream/80 text-ink hover:bg-cream transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>

              <div className="hidden md:block bg-tan/60">
                <img
                  src={`${process.env.PUBLIC_URL}/hero-model-mobile.png`}
                  alt="Prestige Wellness Co"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="p-8 md:p-10 flex flex-col justify-center">
                {!done ? (
                  <>
                    <p className="font-serif italic text-faded text-lg mb-2">Join the ritual</p>
                    <h2 className="font-sans font-extrabold tracking-tight text-3xl md:text-4xl leading-[1.05] text-ink">
                      10% off your <span className="font-serif italic font-medium">first order.</span>
                    </h2>
                    <p className="mt-3 text-faded text-sm leading-relaxed">
                      Subscribe for early access, wellness tips and a welcome discount — wellness worth chewing.
                    </p>
                    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="subscribe-form">
                      <input
                        data-testid="subscribe-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full rounded-full border border-tan bg-cream px-5 py-3.5 text-sm outline-none focus:border-forest transition-colors"
                      />
                      <button
                        data-testid="subscribe-submit"
                        type="submit"
                        disabled={loading}
                        className="group w-full flex items-center justify-center gap-3 rounded-full bg-forest text-cream px-6 py-3.5 text-sm font-bold tracking-widest uppercase transition-colors hover:bg-forest-deep disabled:opacity-60"
                      >
                        {loading ? "Joining…" : "Get my 10% off"}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                      </button>
                    </form>
                    <button onClick={dismiss} data-testid="subscribe-nothanks" className="mt-4 text-xs text-faded hover:text-ink transition-colors underline underline-offset-4">
                      No thanks, I&apos;ll pay full price
                    </button>
                  </>
                ) : (
                  <div data-testid="subscribe-success">
                    <div className="w-12 h-12 rounded-full bg-forest text-cream flex items-center justify-center mb-6">
                      <Check className="w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-sans font-extrabold tracking-tight text-3xl text-ink">You&apos;re in.</h2>
                    <p className="mt-3 text-faded text-sm leading-relaxed">
                      Use this code at checkout for 10% off your first order:
                    </p>
                    <button
                      data-testid="subscribe-code"
                      onClick={copyCode}
                      className="mt-4 w-full flex items-center justify-between gap-3 rounded-xl border-2 border-dashed border-forest/40 bg-cream px-5 py-4 font-sans font-black tracking-[0.2em] text-xl text-forest hover:bg-tan/50 transition-colors"
                    >
                      {DISCOUNT_CODE}
                      <Copy className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <button
                      onClick={dismiss}
                      data-testid="subscribe-start-shopping"
                      className="mt-6 w-full rounded-full bg-forest text-cream px-6 py-3.5 text-sm font-bold tracking-widest uppercase transition-colors hover:bg-forest-deep"
                    >
                      Start shopping
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
